import { writable } from 'svelte/store';
import { browser } from '$app/environment';
import klaviyoAPI from './klaviyoAPI';
import { stripGID } from '$lib/utils/functions/stripGID';

export const InsightWise = writable(); // All the InsightServices ready

let insightQueue = []; // Queue the insights here until Services ready and then use as a polled batch.

function emitBatch() {
    klaviyoAPI.emitBatch(); // Ensure batch is continually cleared if not already handled
    if (insightQueue.length) {
        // Send batch
    }
}

InsightWise.subscribe(async (v) => {
    if (typeof v === 'undefined' || !browser) return; // Uninitialized or server

    emitBatch(); // On init
    setInterval(emitBatch, 20000); // Poll for jobs every 20s
});

/**
 * @description Destructures [[insight], n..] into {type, data, datetime} and pushes to the queue
 * @param {Array.<Array.<String|{}>>} insights
 */
const add = (insights) => {
    const points = typeof insights[0] === 'string' ? [insights] : insights; // Convert singular to List
    const ISODateTime = new Date().toISOString();

    const timestampedInsights = points.map(([type, insightData]) => ({ type, insightData, ISODateTime }));
    //insightQueue.push(...timestampedInsights);
    klaviyoAPI.add(timestampedInsights);
};

// --------------------------------- createInsightEventData ---------------------------

const createInsightItem = {
    product: ({ product, selectedVariant, cms }) => {
        const image = (function () {
            if (selectedVariant?.image?.url) {
                return selectedVariant.image.url;
            } else if (product?.images?.edges?.[0]?.node?.url) {
                return product.images.edges[0].node.url;
            }
            return undefined;
        })();

        const variantTitle = selectedVariant?.selectedOptions?.length
            ? selectedVariant.selectedOptions.reduce((acc, option, i) => `${acc}${i > 0 ? ' / ' : ''}${option?.value}`, '')
            : undefined;

        const productCollections = (() => {
            const collections = [];
            if (product?.productType) collections.push(product.productType);
            if (cms?.linkedStore?.length) {
                const cmsData = cms.linkedStore[0];
                if (cmsData?.productSubType) collections.push(cmsData.productSubType);
                if (cmsData?.tags?.length) collections.push(...cmsData.tags);
            }
            return Array.from(new Set(collections));
        })();

        return {
            productId: product?.id ? stripGID(product.id) : undefined,
            productTitle: product?.title,
            productVendor: product?.vendor,
            productCollections: productCollections,
            productType: product?.productType,
            productHandle: product?.handle,
            sku: selectedVariant?.sku,
            variantImage: image,
            variantId: selectedVariant?.id ? stripGID(selectedVariant.id) : undefined,
            variantTitle,
            price: selectedVariant?.price?.amount ? Number(selectedVariant.price.amount) : undefined, // Shopify needs variant price amount
            compareAtPrice: selectedVariant?.compareAtPrice?.amount ? Number(selectedVariant.compareAtPrice.amount) : undefined, // Shopify needs variant price ammount
            currency: selectedVariant?.price?.currencyCode ? selectedVariant.price.currencyCode : undefined
            //viewListId: '', // "related_products"
            //viewListFriendlyName: '', // "Related Products"
            //indexInViewList: '' // When used in 'related_products' itemList, etc
        };
    },
    cart: (type, { cart, prevCart, change }) => {
        let cartLineId;

        if (type === 'add') {
            [cartLineId] =
                cart?.cart?.lines?.edges?.filter((edge) => edge?.node?.merchandise?.id === change?.[0]?.merchandiseId).map((edge) => edge?.node?.id) || [];
        } else if (type === 'update') {
            cartLineId = change?.id;
        }

        if (!cartLineId) return {}; // Early return

        let lineItemData = cart?.cart?.lines?.edges?.find((edge) => edge?.node?.id === cartLineId)?.node;
        let prevLineItemData = prevCart?.cart?.lines?.edges?.find((edge) => edge?.node?.id === cartLineId)?.node;

        function getChangeType() {
            const newQuantity = lineItemData?.quantity || 0;
            const oldQuantity = prevLineItemData?.quantity || 0;
            const quantity = newQuantity - oldQuantity;
            return { quantity };
        }

        const quantity = type === 'update' ? getChangeType()?.quantity : change?.[0]?.quantity;

        const itemData = lineItemData || prevLineItemData;

        const productCollections = (() => {
            const collections = [];
            if (itemData?.merchandise?.product?.productType) collections.push(itemData.merchandise.product.productType);
            if (itemData?.merchandise?.product) {
                const product = itemData.merchandise.product;
                if (product?.productSubType?.value) collections.push(product.productSubType.value);
                if (product?.tags?.length) collections.push(...product.tags);
            }
            return Array.from(new Set(collections));
        })();

        return {
            productId: itemData?.merchandise?.product?.id ? stripGID(itemData.merchandise.product.id) : undefined,
            productTitle: itemData?.merchandise?.product?.title,
            productVendor: itemData?.merchandise?.product?.vendor,
            productCollections,
            productType: itemData?.merchandise?.product?.productType,
            productSubType: itemData?.merchandise?.product?.productSubType?.value,
            productHandle: itemData?.merchandise?.product?.handle,
            sku: itemData?.merchandise?.sku,
            variantImage: itemData?.merchandise?.image?.url,
            variantId: itemData?.merchandise?.id ? stripGID(itemData.merchandise.id) : undefined,
            variantTitle: itemData?.merchandise?.title,
            price: itemData?.merchandise?.price?.amount ? Number(itemData.merchandise.price.amount) : undefined,
            compareAtPrice: itemData?.merchandise?.compareAtPrice?.amount ? Number(itemData.merchandise.compareAtPrice.amount) : undefined,
            currency: itemData?.merchandise?.price?.currencyCode,
            quantity,
            rowTotal: itemData?.cost?.totalAmount?.amount,
            //discountCoupon: '', // TODO
            //discountValue: '',
            prevLine: prevLineItemData,
            currentLine: lineItemData,
            prevCart,
            cart,
            type: quantity > 0 ? 'add' : 'remove'
        };
    },
    game: (type, gameData) => {
        return {
            type,
            data: {
                answer: gameData?.answer,
                guessCount: gameData?.answers?.length,
                guesses: gameData?.guesses,
                promotion: gameData?.promoDescription
            }
        };
    }
};

// --------------------------------- createInsightEvent ---------------------------

// TODO : Started Checkout / // Cart: , remove_from_cart, view_cart

const createInsight = {
    productView: (productViewData) => ['productView', productViewData],
    itemView: (itemViewData) => ['itemView', itemViewData],
    addToCart: (cartAddData) => ['addToCart', cartAddData],
    game: (gameInsight) => [gameInsight.type, gameInsight.data]
};

// --------------------------------------

export default { createInsight, createInsightItem, add };

// Event-scoped (counts the numver of times an event was triggered)
// item-scoped (counts the number of times interacted with items in event)

/*function createItem(data) {
    return {
        productId: '',
        productTitle: '',
        productVendor: '',
        productCollections: '',
        productType: '',
        productSubType: '',
        productHandle: '',
        sku: '',
        variantImage: '', // Default to product 0 or null if no variant
        variantId: '',
        variantTitle: '',
        price: '', // Shopify needs variant price ammount
        compareAtPrice: '',
        currency: '',
        quantity: '',
        rowTotal: '', //
        discountCoupon: '',
        discountValue: '',
        viewListId: '', // "related_products"
        viewListFriendlyName: '', // "Related Products"
        indexInViewList: '' // When used in 'related_products' itemList, etc
    };
}*/

// Shopify
/*
    ShopAnalytics = {shopId, acceptedLanguage, currency, hydrogenSubChannel}
    BasePayload = {shop: ShopAnalytics}
    CartPayload = {cart, prevCart};
    CartLinePayload = {prevLine,currentLine}
    CartUpdatePayload = {CartPayLoad & BasePayload & OtherData}
    CartLineUpdatePayload = {CartLinePayload & CartPayLoad & BasePayload & OtherData}
*/
